export interface ApInvoiceSplitDraftDefaults {
  vendorInvoiceNumber: string;
  invoiceDate: string;
  totalCost: string;
}

export interface ApInvoiceSplitGroupSeed {
  vendor_invoice_number: string;
  invoice_date: string;
  mfr_invoice_number: string;
  total_cost: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeApInvoiceSplitDraftNumber(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "(draft)") return "";
  return trimmed;
}

export function normalizeApInvoiceSplitDraftDate(raw: string): string {
  const trimmed = raw.trim();
  return ISO_DATE.test(trimmed) ? trimmed : "";
}

/** Blank and zero mean the draft total was not set. */
export function normalizeApInvoiceSplitDraftTotal(raw: string): string {
  const trimmed = raw.trim().replace(/,/g, "");
  if (trimmed === "") return "";
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed === 0) return "";
  return parsed.toFixed(2);
}

/**
 * Copy the vendor-invoice draft onto split groups.
 * The invoice date is the default on every group and stays editable.
 * One group always receives the draft number and total. Several groups receive
 * those only when the draft invoice number matches that group's suggested number.
 */
export function applyApInvoiceSplitDraftDefaults<T extends ApInvoiceSplitGroupSeed>(
  groups: T[],
  draft: ApInvoiceSplitDraftDefaults,
): T[] {
  const number = normalizeApInvoiceSplitDraftNumber(draft.vendorInvoiceNumber);
  const date = normalizeApInvoiceSplitDraftDate(draft.invoiceDate);
  const total = normalizeApInvoiceSplitDraftTotal(draft.totalCost);
  if (groups.length === 0 || (number === "" && date === "" && total === "")) {
    return groups;
  }

  const matchingIndexes = new Set<number>();
  if (number !== "") {
    groups.forEach((group, index) => {
      const suggested = group.mfr_invoice_number.trim();
      const vendorNumber = group.vendor_invoice_number.trim();
      if (suggested === number || vendorNumber === number) {
        matchingIndexes.add(index);
      }
    });
  }

  const targetIndexes =
    groups.length === 1 ? new Set([0]) : matchingIndexes;

  return groups.map((group, index) => {
    const withDate = date !== "" ? { ...group, invoice_date: date } : group;
    if (!targetIndexes.has(index)) return withDate;
    return {
      ...withDate,
      vendor_invoice_number: number !== "" ? number : withDate.vendor_invoice_number,
      mfr_invoice_number: number !== "" ? number : withDate.mfr_invoice_number,
      total_cost: total !== "" ? total : withDate.total_cost,
    };
  });
}
